import * as React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva } from 'class-variance-authority';
import { X, Check } from 'lucide-react';

import { cn } from 'src/lib/utils';

const ToastProvider = ToastPrimitives.Provider;

// Viewport must sit above everything: sidebar (99999), edit modal (100001), portaled dropdowns (100050).
const ToastViewport = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed top-4 left-1/2 -translate-x-1/2 z-[200000] flex max-h-screen flex-col gap-2 w-fit min-w-0 max-w-[calc(100vw-2rem)]',
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  'group pointer-events-auto relative flex w-auto items-center justify-between space-x-3 overflow-hidden rounded-md border border-black/70 bg-ah-status-toast-bg text-slate-100 shadow-[0_8px_28px_rgba(0,0,0,0.35)] leading-tight transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-top-full data-[state=open]:slide-in-from-top-full',
  {
    variants: {
      variant: {
        default: 'border-black/70 bg-ah-status-toast-bg text-slate-100',
        success: 'border-black/70 bg-ah-status-toast-bg text-slate-100',
        info: 'border-black/70 bg-ah-status-toast-bg text-slate-100',
        warning: 'border-black/70 bg-ah-status-toast-bg text-slate-100',
        destructive: 'border-black/70 bg-ah-status-toast-bg text-slate-100',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Toast = React.forwardRef(
  ({ className, variant, style, ...props }, ref) => {
    return (
      <ToastPrimitives.Root
        ref={ref}
        className={cn(toastVariants({ variant }), className)}
        style={{
          padding: '0.5rem 1rem',
          lineHeight: '1.25',
          width: 'fit-content',
          minWidth: 'unset',
          maxWidth: 'calc(100vw - 2rem)',
          ...style,
        }}
        {...props}
      />
    );
  }
);
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive',
      className
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'rounded-md p-1 text-slate-100/50 opacity-70 transition-opacity hover:text-slate-100 hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 ml-2',
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-3 w-3" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn(
      'text-sm font-normal text-slate-100 whitespace-nowrap line-clamp-1',
      className
    )}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn(
      'text-sm font-normal text-slate-100 whitespace-nowrap line-clamp-1',
      className
    )}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

// Toast Icon component for different variants
const ToastIcon = ({ variant }) => {
  const iconClasses = 'h-4 w-4 flex-shrink-0';

  switch (variant) {
    case 'success':
      return <Check className="h-4 w-4" />;
    case 'destructive':
      return <X className="h-4 w-4" />;
    default:
      return null;
  }
};

export {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
  ToastIcon,
};
